import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab, Chip,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  MenuItem, Select, InputLabel, FormControl, CircularProgress,
  Checkbox, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAppContext } from '../../context/AppContext';



const ProjectList = () => {
  // Get query parameters
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('id');

  // Use the shared context
  const {
    projects,
    users,
    loading,
    error,
    addProject,
    updateProject,
    deleteProject,
    assignUserToProject,
    removeUserFromProject,
    getProjectUsers
  } = useAppContext();

  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [currentProject, setCurrentProject] = useState({
    name: '',
    description: '',
    job_number: '',
    department_id: '',
    project_lead_id: '',
    allowable_hours: '',
    overhead_percentage: '',
    director_comments: ''
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newAssignment, setNewAssignment] = useState({
    user_id: '',
    role: 'trainee_quantity_surveyor',
    project_lead: false
  });


  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        console.log('🔍 Fetching departments...');
        const response = await axios.get('/api/departments', { withCredentials: true });
        console.log('✅ Departments fetched:', response.data);
        setDepartments(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('❌ Error fetching departments:', err);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Find the project from URL parameter
  useEffect(() => {
    if (projectIdFromUrl && projects.length > 0) {
      const project = projects.find(p => p.id === parseInt(projectIdFromUrl) || p.id === projectIdFromUrl);
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [projectIdFromUrl, projects]);

  // Fetch project users when a project is selected
  useEffect(() => {
    if (selectedProject) {
      const projectUsersList = getProjectUsers(selectedProject.id);
      setProjectUsers(projectUsersList);
    }
  }, [selectedProject, getProjectUsers]);

  const handleOpenDialog = (project = null) => {
    if (project) {
      setCurrentProject(project);
      setIsEditing(true);
    } else {
      setCurrentProject({
        name: '',
        description: '',
        job_number: '',
        department_id: '',
        project_lead_id: '',
        allowable_hours: '',
        overhead_percentage: '',
        director_comments: ''
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Handle numeric fields
    if (['allowable_hours', 'overhead_percentage'].includes(name)) {
      processedValue = value === '' ? '' : parseFloat(value);
    }

    setCurrentProject({
      ...currentProject,
      [name]: processedValue
    });
  };

  const handleSubmit = async () => {
    try {
      // Validate job number is not duplicate (when creating or changing job number)
      if (currentProject.job_number && currentProject.job_number.trim() !== '') {
        const existingProject = projects.find(p =>
          p.job_number === currentProject.job_number.trim() &&
          p.id !== currentProject.id
        );
        if (existingProject) {
          alert(`Job number ${currentProject.job_number} already exists for project "${existingProject.name}". Please use a unique job number.`);
          return;
        }
      }

      // Include project data with defaults for new projects
      const projectData = {
        ...currentProject,
        // Set default invoiced amount to 0 for new projects
        invoiced_amount: isEditing ? currentProject.invoiced_amount : 0
      };

      if (isEditing) {
        const updatedProject = await updateProject(currentProject.id, projectData);
        if (selectedProject && selectedProject.id === updatedProject.id) {
          setSelectedProject(updatedProject);
        }
      } else {
        const newProject = await addProject(projectData);
        setSelectedProject(newProject);
        // Update URL to include the new project ID
        navigate(`/projects?id=${newProject.id}`);
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving project:', err);
      // Handle database constraint error gracefully
      if (err.response?.data?.error?.includes('unique_job_number') ||
          err.message?.includes('unique_job_number') ||
          err.response?.data?.error?.includes('duplicate key')) {
        alert('This job number already exists. Please use a unique job number.');
      } else {
        alert('Failed to save project. Please try again.');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setTabValue(1); // Switch to team tab
  };

  const handleOpenAssignDialog = () => {
    setNewAssignment({
      user_id: '',
      role: 'trainee_quantity_surveyor',
      project_lead: false
    });
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
  };

  const handleAssignmentChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If user is selected, automatically get their role
    if (name === 'user_id' && value) {
      const selectedUser = users.find(u => u.id === parseInt(value));
      if (selectedUser) {
        setNewAssignment({
          ...newAssignment,
          user_id: value,
          role: selectedUser.role || 'trainee_quantity_surveyor' // Use user's existing role
        });
        return;
      }
    }

    setNewAssignment({
      ...newAssignment,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAssignUser = async () => {
    try {
      await assignUserToProject(
        selectedProject.id,
        newAssignment.user_id,
        newAssignment.role,
        newAssignment.project_lead
      );
      // Refresh project users
      const updatedProjectUsers = getProjectUsers(selectedProject.id);
      setProjectUsers(updatedProjectUsers);
      handleCloseAssignDialog();
    } catch (err) {
      console.error('Error assigning user:', err);
    }
  };

  const handleRemoveUser = async (assignmentId) => {
    try {
      await removeUserFromProject(assignmentId);
      // Refresh project users
      const updatedProjectUsers = getProjectUsers(selectedProject.id);
      setProjectUsers(updatedProjectUsers);
    } catch (err) {
      console.error('Error removing user:', err);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This will delete all associated data including team assignments, tasks, and timesheets. This action cannot be undone.')) {
      try {
        await deleteProject(projectId);

        // If the deleted project is currently selected, clear the selection
        if (selectedProject && selectedProject.id === projectId) {
          setSelectedProject(null);
          navigate('/projects');
        }
      } catch (err) {
        console.error('Error deleting project:', err);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading projects...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography>
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Projects
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ mr: 2 }}
          >
            Add Project
          </Button>
        </Box>
      </Box>

      {selectedProject ? (
        <Box sx={{ width: '100%', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">{selectedProject.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedProject.description}</Typography>
              <Typography variant="body2">Allowable Hours: {selectedProject.allowable_hours || 'Not Set'}</Typography>
            </Box>
            <Button variant="outlined" onClick={() => setSelectedProject(null)}>
              Back to Projects
            </Button>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="project tabs">
              <Tab label="Details" id="project-tab-0" />
              <Tab label="Team Members" id="project-tab-1" />
            </Tabs>
          </Box>

          {/* Details Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Project Details</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="h6" color="primary" gutterBottom>Basic Information</Typography>
                  <Typography sx={{ mb: 1 }}><strong>Name:</strong> {selectedProject.name}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>Description:</strong> {selectedProject.description}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>Job Number:</strong> {selectedProject.job_number || '-'}</Typography>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Department:</strong> {
                      selectedProject.department_id
                        ? departments.find(d => d.id === selectedProject.department_id)?.name || '-'
                        : '-'
                    }
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" color="primary" gutterBottom>Project Management</Typography>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Allowable Hours:</strong> {selectedProject.allowable_hours || 'Not Set'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>Additional Information</Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Created:</strong> {new Date(selectedProject.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(selectedProject)}
                >
                  Edit Project
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => handleDeleteProject(selectedProject.id)}
                >
                  Delete Project
                </Button>
              </Box>
            </Box>
          )}

          {/* Team Members Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>Team Members</Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAssignDialog}
                >
                  Assign User
                </Button>
              </Box>

              <List>
                {projectUsers.map((assignment) => {
                  const user = users.find(u => u.id === assignment.user_id);
                  const isProjectLead = user?.project_lead === 1;

                  return (
                    <React.Fragment key={assignment.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1">{assignment.user_name}</Typography>
                              {isProjectLead && (
                                <Chip
                                  label="Project Lead"
                                  size="small"
                                  color="primary"
                                  variant="filled"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Role: {user?.role || assignment.role}
                              </Typography>
                              {user?.department_name && (
                                <Typography variant="body2" color="text.secondary">
                                  Department: {user.department_name}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveUser(assignment.id)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })}
                {projectUsers.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No team members assigned to this project." />
                  </ListItem>
                )}
              </List>
            </Box>
          )}


        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Job Number</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Allowable Hours</TableCell>
                <TableCell>Overhead %</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow
                  key={project.id}
                  hover
                  onClick={() => handleProjectSelect(project)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{project.name}</TableCell>
                  <TableCell>{project.job_number || '-'}</TableCell>
                  <TableCell>
                    {project.department_id
                      ? departments.find(d => d.id === project.department_id)?.name || '-'
                      : '-'
                    }
                  </TableCell>
                  <TableCell>{project.description}</TableCell>
                  <TableCell>{project.allowable_hours ? project.allowable_hours.toFixed(2) : '-'}</TableCell>
                  <TableCell>{project.overhead_percentage ? `${project.overhead_percentage}%` : '-'}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(project);
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {projects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No projects found. Add some projects to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Project Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{isEditing ? 'Edit Project' : 'Add Project'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={currentProject.name}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              multiline
              rows={3}
              value={currentProject.description}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Job Number"
              name="job_number"
              value={currentProject.job_number || ''}
              onChange={handleInputChange}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="department-select-label">Department</InputLabel>
              <Select
                labelId="department-select-label"
                name="department_id"
                value={currentProject.department_id || ''}
                onChange={handleInputChange}
                label="Department"
              >
                <MenuItem value=""><em>Select a department</em></MenuItem>
                {Array.isArray(departments) && departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="project-lead-select-label">Project Lead</InputLabel>
              <Select
                labelId="project-lead-select-label"
                name="project_lead_id"
                value={currentProject.project_lead_id || ''}
                onChange={handleInputChange}
                label="Project Lead"
              >
                <MenuItem value=""><em>Select a project lead</em></MenuItem>
                {Array.isArray(users) && users.filter(user => user.project_lead === 1 || user.project_lead === true).map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Allowable Hours"
              name="allowable_hours"
              type="number"
              value={currentProject.allowable_hours || ''}
              onChange={handleInputChange}
              inputProps={{ step: '0.01', min: '0' }}
              helperText="Administrator only field"
            />

            {/* Financial Information Section */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
              Financial Information
            </Typography>

            <TextField
              fullWidth
              margin="normal"
              label="Overhead Percentage (%)"
              name="overhead_percentage"
              type="number"
              value={currentProject.overhead_percentage || ''}
              onChange={handleInputChange}
              inputProps={{ step: '0.1', min: '0', max: '100' }}
              helperText="Default overhead percentage for reports"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Director Comments"
              name="director_comments"
              multiline
              rows={3}
              value={currentProject.director_comments || ''}
              onChange={handleInputChange}
              placeholder="Enter comments about project status, invoicing, or other notes..."
              helperText="Director's notes on project progress and invoicing status"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={openAssignDialog} onClose={handleCloseAssignDialog}>
        <DialogTitle>Assign User to Project</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="user-select-label">User</InputLabel>
              <Select
                labelId="user-select-label"
                name="user_id"
                value={newAssignment.user_id}
                onChange={handleAssignmentChange}
                label="User"
              >
                <MenuItem value=""><em>Select a user</em></MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" sx={{ display: 'none' }}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                name="role"
                value={newAssignment.role}
                onChange={handleAssignmentChange}
                label="Role"
              >
                <MenuItem value="trainee_project_manager">Trainee Project Manager</MenuItem>
                <MenuItem value="trainee_quantity_surveyor">Trainee Quantity Surveyor</MenuItem>
                <MenuItem value="junior_project_manager">Junior Project Manager</MenuItem>
                <MenuItem value="junior_quantity_surveyor">Junior Quantity Surveyor</MenuItem>
                <MenuItem value="intermediate_project_manager">Intermediate Project Manager</MenuItem>
                <MenuItem value="intermediate_quantity_surveyor">Intermediate Quantity Surveyor</MenuItem>
                <MenuItem value="senior_project_manager">Senior Project Manager</MenuItem>
                <MenuItem value="senior_quantity_surveyor">Senior Quantity Surveyor</MenuItem>
                <MenuItem value="professional_project_manager">Professional Project Manager</MenuItem>
                <MenuItem value="professional_quantity_surveyor">Professional Quantity Surveyor</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="director">Director</MenuItem>
                <MenuItem value="project_administrator">Project Administrator</MenuItem>
                <MenuItem value="general_administration">General Administration</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              sx={{ display: 'none', mt: 2 }}
              control={
                <Checkbox
                  checked={newAssignment.project_lead}
                  onChange={handleAssignmentChange}
                  name="project_lead"
                />
              }
              label="Project Lead"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button
            onClick={handleAssignUser}
            variant="contained"
            disabled={!newAssignment.user_id}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectList;
